import type { LayoutProps } from "../types";

export default function TabloidLayout({ theme, template, articles, language }: LayoutProps) {
  const t = theme;
  const [hero, ...rest] = articles;
  const top = rest.slice(0, 3);
  const strip = rest.slice(3);
  const exclusiveLabel = language === "en" ? "EXCLUSIVE" : "EXCLUSIF";
  const moreLabel = language === "en" ? "MORE INSIDE" : "AUSSI DANS CE NUMERO";

  return (
    <div style={{ background: t.palette.bg, color: t.palette.ink, fontFamily: t.typography.body }}>
      {/* Aggressive masthead with thick filets */}
      <header className="relative">
        <div style={{ background: t.palette.accent, color: t.palette.accentInk }} className="px-4 py-1 text-center text-xs font-bold uppercase tracking-[0.4em]">
          {exclusiveLabel} &middot; {hero?.date}
        </div>
        <div style={{ background: t.palette.ink, color: t.palette.bg }} className="px-6 py-6">
          <h1
            style={{
              fontFamily: t.typography.masthead,
              letterSpacing: "-0.04em",
              lineHeight: "0.85",
            }}
            className="text-5xl font-black uppercase md:text-7xl"
          >
            {template.name}
          </h1>
        </div>
        <div style={{ background: t.palette.accent }} className="h-2" />
      </header>

      {/* Hero manchette - massive headline, image right */}
      {hero && (
        <section style={{ borderBottom: `4px solid ${t.palette.ink}` }} className="grid gap-0 md:grid-cols-5">
          <div className="md:col-span-3 md:pr-6">
            <div className="px-6 py-8">
              <span
                style={{ background: t.palette.accent, color: t.palette.accentInk, fontFamily: t.typography.meta, letterSpacing: "0.2em" }}
                className="inline-block px-3 py-1 text-xs font-bold uppercase"
              >
                {hero.kicker}
              </span>
              <h2
                style={{
                  fontFamily: t.typography.masthead,
                  color: t.palette.ink,
                  letterSpacing: "-0.04em",
                  lineHeight: "0.9",
                }}
                className="mt-4 text-5xl font-black uppercase md:text-7xl"
              >
                {hero.title}
              </h2>
              <div style={{ background: t.palette.accent }} className="my-5 h-1 w-full" />
              <p style={{ fontFamily: t.typography.body, color: t.palette.ink }} className="text-lg font-semibold leading-7 md:text-xl">
                {hero.dek}
              </p>
              <p style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.12em" }} className="mt-3 text-[11px] uppercase">
                Par {hero.byline} &middot; {hero.date}
              </p>
            </div>
          </div>
          <div className="md:col-span-2">
            <img src={hero.imageUrl} alt="" className="h-full max-h-[520px] w-full object-cover" />
          </div>
        </section>
      )}

      {/* Three-column dense top stories */}
      <section style={{ borderBottom: `4px solid ${t.palette.ink}` }} className="grid grid-cols-1 gap-0 md:grid-cols-3">
        {top.map((a, i) => (
          <article
            key={a.id}
            className="p-5"
            style={{
              borderRight: i < top.length - 1 ? `2px solid ${t.palette.ink}` : "none",
            }}
          >
            <span style={{ background: t.palette.ink, color: t.palette.bg, fontFamily: t.typography.meta, letterSpacing: "0.18em" }} className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase">
              {a.kicker}
            </span>
            <h3 style={{ fontFamily: t.typography.masthead, color: t.palette.ink, letterSpacing: "-0.025em", lineHeight: "0.95" }} className="mt-2 text-2xl font-black uppercase md:text-3xl">
              {a.title}
            </h3>
            <div style={{ background: t.palette.accent }} className="my-3 h-0.5 w-12" />
            <p style={{ fontFamily: t.typography.body, color: t.palette.ink }} className="text-sm leading-6">
              {a.dek}
            </p>
          </article>
        ))}
      </section>

      {/* Bottom dense strip - 4 cols compact */}
      <section className="px-5 py-6">
        <h3 style={{ background: t.palette.ink, color: t.palette.bg, fontFamily: t.typography.meta, letterSpacing: "0.32em" }} className="mb-4 inline-block px-3 py-1 text-[11px] font-bold uppercase">
          {moreLabel}
        </h3>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 md:grid-cols-4">
          {strip.map((a) => (
            <article key={a.id} style={{ borderTop: `2px solid ${t.palette.ink}` }} className="pt-3">
              <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.18em" }} className="text-[10px] font-bold uppercase">
                {a.kicker}
              </span>
              <h4 style={{ fontFamily: t.typography.masthead, color: t.palette.ink, letterSpacing: "-0.02em", lineHeight: "1" }} className="mt-1 text-base font-black uppercase">
                {a.title}
              </h4>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
